/** Présentation collaborative (deck minimal, Yjs). */
import { el, clear, escapeHtml } from '../ui/dom.js';
import { icon } from '../ui/icons.js';
import { Drive } from '../api.js';
import { connectDoc, renderPresence, Y } from '../collab.js';
import { shareModal } from './drive.js';

export function renderSlides(ctx) {
  const { content, params, user, navigate } = ctx;
  const nodeId = params.id;
  let provider, doc, stopPresence, saveTimer, destroyed = false;
  let current = 0;

  content.style.padding = '0';
  const root = el('div', { class: 'editor-wrap' });
  content.appendChild(root);

  (async () => {
    let node;
    try { ({ node } = await Drive.node(nodeId)); }
    catch (e) { root.appendChild(el('div', { class: 'empty' }, el('h3', {}, 'Présentation introuvable'), el('div', {}, e.message))); return; }
    if (destroyed) return;
    const readOnly = node.role === 'viewer';
    ctx.setCrumbs([{ label: 'Mon Drive', path: '/drive' }, { label: node.name }]);

    const conn = connectDoc(nodeId, user); doc = conn.doc; provider = conn.provider;
    const slides = doc.getArray('slides');
    const ytitle = doc.getText('title');

    const saveState = el('div', { class: 'save-state' }, el('span', { class: 'dot' }), el('span', {}, 'Connexion…'));
    const presence = el('div', { class: 'presence' });
    const titleInput = el('input', { class: 'input', style: { maxWidth: '320px', fontWeight: 600, border: 'none', background: 'transparent', fontSize: '16px' }, value: node.name });
    titleInput.disabled = readOnly;
    const back = el('button', { class: 'btn btn-sm btn-ghost', html: icon('back', 16), onclick: () => navigate('/drive') });
    const addBtn = el('button', { class: 'btn btn-sm', html: icon('plus', 16) + 'Diapo', onclick: () => { if (readOnly) return; doc.transact(() => slides.push([newSlide()])); current = slides.length - 1; render(); } });
    const share = el('button', { class: 'btn btn-sm', html: icon('share', 16) + 'Partager', onclick: () => shareModal(node) });
    root.appendChild(el('div', { style: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 18px', borderBottom: '1px solid var(--hairline)' } },
      back, titleInput, saveState, el('div', { style: { flex: 1 } }), presence, readOnly ? '' : addBtn, node.role === 'owner' ? share : ''));

    const rail = el('div', { class: 'slide-rail' });
    const stage = el('div', { class: 'slide-stage' });
    root.appendChild(el('div', { class: 'slides-wrap' }, rail, stage));

    function newSlide() { const m = new Y.Map(); m.set('title', ''); m.set('body', ''); return m; }

    function render() {
      if (slides.length === 0) { clear(rail); clear(stage); stage.appendChild(el('div', { style: { color: 'var(--text-faint)' } }, 'Ajoutez une diapositive.')); return; }
      current = Math.max(0, Math.min(current, slides.length - 1));
      renderRail(); renderStage();
    }
    function renderRail() {
      clear(rail);
      slides.forEach((m, i) => {
        const thumb = el('div', { class: 'slide-thumb' + (i === current ? ' active' : '') },
          el('div', { style: { fontWeight: 700, marginBottom: '3px' } }, m.get('title') || 'Sans titre'),
          el('div', { style: { opacity: 0.7 } }, (m.get('body') || '').slice(0, 60)),
          el('span', { class: 'n' }, String(i + 1)));
        thumb.addEventListener('click', () => { current = i; render(); });
        if (!readOnly) thumb.addEventListener('contextmenu', (e) => { e.preventDefault(); if (slides.length > 1 && confirm('Supprimer cette diapositive ?')) { doc.transact(() => slides.delete(i, 1)); current = Math.max(0, i - 1); render(); } });
        rail.appendChild(thumb);
      });
    }
    function renderStage() {
      clear(stage);
      const m = slides.get(current);
      const t = el('div', { class: 's-title', contenteditable: !readOnly, 'data-ph': 'Titre de la diapo' });
      const b = el('div', { class: 's-body', contenteditable: !readOnly, 'data-ph': 'Contenu…' });
      t.textContent = m.get('title') || ''; b.textContent = m.get('body') || '';
      t.addEventListener('input', () => doc.transact(() => m.set('title', t.textContent)));
      b.addEventListener('input', () => doc.transact(() => m.set('body', b.textContent)));
      stage.appendChild(el('div', { class: 'slide-canvas' }, t, b));
    }

    provider.once('synced', () => { if (slides.length === 0 && !readOnly) doc.transact(() => slides.push([newSlide()])); render(); });
    slides.observeDeep(() => {
      const active = document.activeElement;
      const editing = active && (active.classList.contains('s-title') || active.classList.contains('s-body'));
      renderRail();
      if (!editing) renderStage();
    });
    render();

    // Titre
    provider.once('synced', () => { if (ytitle.length === 0 && node.name) doc.transact(() => ytitle.insert(0, node.name)); if (document.activeElement !== titleInput) titleInput.value = ytitle.toString() || node.name; });
    ytitle.observe(() => { if (document.activeElement !== titleInput) titleInput.value = ytitle.toString(); });
    if (!readOnly) titleInput.addEventListener('input', () => { const v = titleInput.value; doc.transact(() => { ytitle.delete(0, ytitle.length); ytitle.insert(0, v); }); clearTimeout(saveTimer); saveTimer = setTimeout(() => Drive.update(nodeId, { name: v || 'Sans titre' }).catch(() => {}), 700); });

    stopPresence = renderPresence(presence, provider, user.id);
    const setState = (s, sy) => { saveState.classList.toggle('syncing', !!sy); saveState.lastChild.textContent = s; };
    provider.on('status', (e) => setState(e.status === 'connected' ? 'Enregistré dans JHN' : 'Reconnexion…', e.status !== 'connected'));
    let tt; doc.on('update', () => { setState('Synchronisation…', true); clearTimeout(tt); tt = setTimeout(() => setState('Enregistré dans JHN', false), 900); });
  })();

  return () => {
    destroyed = true; content.style.padding = ''; clearTimeout(saveTimer);
    try { stopPresence && stopPresence(); } catch {}
    try { provider && provider.destroy(); } catch {}
    try { doc && doc.destroy(); } catch {}
  };
}
